// 1. Core & External Libraries
import type { BottomTabNavigationProp } from '@react-navigation/bottom-tabs';
import { useNavigation, type CompositeNavigationProp } from '@react-navigation/native';
import type { NativeStackNavigationProp } from '@react-navigation/native-stack';
import React, { memo, useCallback, useEffect, useMemo, useState } from 'react';
import { RefreshControl } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';

import { FLOATING_TAB_BAR_CLEARANCE_TOKEN } from '@/components/ui/tabbar';

// 2. State, Queries, & Navigation
import type { GuardianStackParamList, GuardianTabNavigatorParamList } from '@/navigations/types';
import { useUiStore } from '@/stores';

// 3. Hooks & Helpers
import { createStyles, useScaling } from '@/hooks';

// 4. UI Components (Shared)
import { Scroll, Stack } from '@/components/layout';
import { showAlert, Text } from '@/components/ui';
import { STRINGS } from '@/constants/strings';

// 5. UI Components (Feature Specific)
import EmptyKidsSection from '@/features/guardian/components/empty-kids-section';
import FamilyActivitySection from '@/features/guardian/components/family-activity-section';
import GetStartedSection from '@/features/guardian/components/get-started-section';
import LessonCarousel, {
  MOCK_GUARDIAN_LESSON_CAROUSEL_ITEMS,
  type GuardianLessonCarouselItem,
} from '@/features/guardian/components/lesson-carousel';
import GuardianKidCard from '@/features/guardian/components/my-kid-card';
import UpcomingWeekSection, {
  type UpcomingWeekItem,
} from '@/features/guardian/components/upcoming-week-section';
import UserProfileHeader from '@/features/guardian/components/user-profile-header';
import WelcomeBanner from '@/features/guardian/components/welcome-banner';
import {
  useGuardianHomePlayers,
  type KidCardData,
} from '@/features/guardian/hooks/use-guardian-home-players';

import MyKidsSection from '@/features/guardian/components/my-kids-section';
import NewNotesSection from '@/features/guardian/components/new-notes-section';
import type { HomeStep } from '@/features/guardian/components/welcome-banner/types';
import { useFamilyEvents } from '@/features/guardian/hooks/use-family-events';
import { useGuardianNotificationBadge } from '@/features/guardian/hooks/use-guardian-notifications';
import { useOpenEventDetail } from '@/features/guardian/hooks/use-open-event-detail';
import { resolveFamilyEvent } from '@/features/guardian/utils/resolve-family-event';
import { guardianColors } from '@/theme/colors';

type GuardianHomeNavigation = CompositeNavigationProp<
  BottomTabNavigationProp<GuardianTabNavigatorParamList, 'Home'>,
  NativeStackNavigationProp<GuardianStackParamList>
>;

type KidsSectionProps = {
  hasPlayers: boolean;
  showOnboarding: boolean;
  onAddPlayerPress: () => void;
  onKidPress?: () => void;
  kidCard: KidCardData;
};

const KidsSection = memo(
  ({ hasPlayers, showOnboarding, onAddPlayerPress, onKidPress, kidCard }: KidsSectionProps) => {
    const { scale, isTablet } = useScaling();
    const styles = useStyles();

    if (!hasPlayers) {
      return (
        <Stack
          paddingTop={scale({ phone: showOnboarding ? 0 : 16, tablet: showOnboarding ? 0 : 16 })}
          paddingX={scale({ phone: 0, tablet: showOnboarding ? 0 : 20 })}>
          <EmptyKidsSection onAddPlayerPress={onAddPlayerPress} />
        </Stack>
      );
    }

    // Welcome flow: a single horizontal player card (no Add-player tile).
    if (showOnboarding) {
      return (
        <Stack paddingX={scale({ phone: 20, tablet: 0 })}>
          <Text variant="Font Guardian App/Body/Body 16 Bold" style={styles.sectionTitleKids}>
            {STRINGS.home.guardian.myKids}
          </Text>
          <GuardianKidCard
            name={kidCard.name}
            level={kidCard.level as any}
            progress={kidCard.progress}
            avatarSource={kidCard.avatarUrl ? { uri: kidCard.avatarUrl } : undefined}
            orientation="horizontal"
            onPress={onKidPress}
          />
        </Stack>
      );
    }
    return (
      <>
        <MyKidsSection onAddPlayer={onAddPlayerPress} />
        {isTablet && <NewNotesSection />}
      </>
    );
  },
);
KidsSection.displayName = 'KidsSection';

function GuardianHomeBody() {
  const { scale, isTablet } = useScaling();
  const insets = useSafeAreaInsets();
  const styles = useStyles();
  const navigation = useNavigation<GuardianHomeNavigation>();

  // ── Data ──────────────────────────────────────────────────────────────────
  const {
    refetchGuardianPlayers,
    guardianPlayers,
    hasPlayers,
    playerName,
    playerAvatarUrl,
    kidCard,
    showOnboarding,
    dismissWelcome,
  } = useGuardianHomePlayers();
  const { events } = useFamilyEvents();
  const openEventDetail = useOpenEventDetail();

  const [isRefreshing, setIsRefreshing] = useState(false);
  const [upcomingWeekItems, _setUpcomingWeekItems] = useState<UpcomingWeekItem[]>([]);

  const hasLessons = upcomingWeekItems.length > 0;

  // ── Handlers ──────────────────────────────────────────────────────────────
  const handleRefresh = useCallback(async () => {
    setIsRefreshing(true);

    try {
      await Promise.all([
        // Keep refresh sources centralized as more home sections become API-backed.
        refetchGuardianPlayers(),
      ]);
    } finally {
      setIsRefreshing(false);
    }
  }, [refetchGuardianPlayers]);

  const handleAddPlayerPress = useCallback(() => {
    navigation.navigate('AddPlayer');
  }, [navigation]);

  const handleKidPress = useCallback(() => {
    const player = guardianPlayers?.[0];
    if (!player) return;
    navigation.navigate('PlayerDetail', { player });
  }, [guardianPlayers, navigation]);

  const handleBookClassPress = useCallback(() => {
    // if (upcomingWeekItems.length === 0) {
    //   setUpcomingWeekItems([]);
    // return;
    // }
    navigation.jumpTo('Book');
  }, [navigation]);

  const handleCtaPress = useCallback(() => {
    if (!hasPlayers) {
      handleAddPlayerPress();
      return;
    }
    // Tapping Book / Track dismisses the Get Started guide (close it early).
    dismissWelcome();
    navigation.jumpTo('Book');
  }, [hasPlayers, handleAddPlayerPress, dismissWelcome, navigation]);

  const handleStepPress = useCallback(
    (step: HomeStep) => {
      if (step === 1) {
        // Step 1 is already completed once a player exists — don't reopen the create screen.
        if (hasPlayers) return;
        handleAddPlayerPress();
      } else {
        // Booking the first lesson completes the Welcome flow.
        dismissWelcome();
        navigation.jumpTo('Book');
      }
    },
    [hasPlayers, handleAddPlayerPress, dismissWelcome, navigation],
  );

  const handleViewDetailsPress = useCallback(
    (item: GuardianLessonCarouselItem) => {
      const lessonPlayerName = item.label.split("'s")[0];
      const event =
        resolveFamilyEvent(events, { playerName: lessonPlayerName, type: 'practice' }) ??
        resolveFamilyEvent(events, { type: 'practice' });
      if (event) openEventDetail(event);
    },
    [events, openEventDetail],
  );

  const handleUpcomingItemPress = useCallback(
    (item: UpcomingWeekItem) => {
      const type = item.title.toLocaleLowerCase() === 'practice' ? 'practice' : 'tournament';
      const event =
        resolveFamilyEvent(events, { playerName: item.playerName, type }) ??
        resolveFamilyEvent(events, { type });
      if (event) openEventDetail(event);
    },
    [events, openEventDetail],
  );

  const handleReschedulePress = useCallback(() => {
    showAlert({ title: 'Reschedule', message: 'Open reschedule' });
  }, []);

  const handleLocationPress = useCallback(() => {
    showAlert({ title: 'Maps', message: 'Open maps' });
  }, []);

  const currentStep = useMemo<HomeStep>(() => {
    if (!hasPlayers) return 1;
    if (!hasLessons) return 2;
    return 3;
  }, [hasPlayers, hasLessons]);

  // The tablet tab bar floats over the content, so each column has to end above it —
  // otherwise the last card sits under the bar with nothing to scroll it out from behind.
  const tabletColumnBottomInset = insets.bottom + scale(FLOATING_TAB_BAR_CLEARANCE_TOKEN);

  if (isTablet) {
    return (
      <Stack flex={1} direction="row">
        {/* Column 1 – Banner + Kids */}
        <Scroll
          style={styles.tabletScroll}
          gap={scale(16)}
          contentContainerStyle={[
            styles.tabletScrollContent,
            {
              paddingHorizontal: showOnboarding ? scale(20) : undefined,
              paddingTop: showOnboarding ? scale(16) : undefined,
              paddingBottom: tabletColumnBottomInset,
            },
          ]}
          refreshControl={<RefreshControl refreshing={isRefreshing} onRefresh={handleRefresh} />}
          showsVerticalScrollIndicator={false}>
          {showOnboarding && (
            <WelcomeBanner
              currentStep={currentStep}
              playerName={playerName}
              onCtaPress={handleCtaPress}
            />
          )}
          <KidsSection
            hasPlayers={hasPlayers}
            showOnboarding={showOnboarding}
            onAddPlayerPress={handleAddPlayerPress}
            onKidPress={handleKidPress}
            kidCard={kidCard}
          />
        </Scroll>

        {/* Column 2 – Get Started + Upcoming */}
        <Scroll
          style={styles.tabletScroll}
          contentContainerStyle={[
            styles.tabletScrollContent,
            {
              paddingTop: scale(16),
              paddingRight: scale(20),
              paddingBottom: tabletColumnBottomInset,
            },
          ]}
          gap={scale(16)}
          showsVerticalScrollIndicator={false}>
          {showOnboarding && (
            <GetStartedSection
              currentStep={currentStep}
              playerName={kidCard.name || playerName}
              playerAvatarUrl={playerAvatarUrl}
              onStepPress={handleStepPress}
            />
          )}
          {currentStep >= 2 && (
            <UpcomingWeekSection
              items={upcomingWeekItems}
              onBookClassPress={handleBookClassPress}
              onItemPress={handleUpcomingItemPress}
              style={styles.tabletSecondScreen}
            />
          )}
        </Scroll>

        {/* Column 3 – Family activity */}
        <FamilyActivitySection />
      </Stack>
    );
  }

  return (
    <Scroll
      style={styles.mobileScroll}
      showsVerticalScrollIndicator={false}
      refreshControl={<RefreshControl refreshing={isRefreshing} onRefresh={handleRefresh} />}
      contentContainerStyle={{ paddingBottom: insets.bottom + scale(40) }}>
      {/* Banner */}
      {showOnboarding && (
        <Stack paddingX={scale(20)} paddingTop={scale(16)} paddingBottom={scale(12)}>
          <WelcomeBanner
            currentStep={currentStep}
            playerName={playerName}
            onCtaPress={handleCtaPress}
          />
        </Stack>
      )}

      {/* Get Started */}
      {showOnboarding && (
        <Stack paddingX={scale(20)} paddingBottom={scale(16)} paddingTop={scale(16)}>
          <GetStartedSection
            currentStep={currentStep}
            playerName={kidCard.name || playerName}
            playerAvatarUrl={playerAvatarUrl}
            onStepPress={handleStepPress}
          />
        </Stack>
      )}

      <KidsSection
        hasPlayers={hasPlayers}
        showOnboarding={showOnboarding}
        onAddPlayerPress={handleAddPlayerPress}
        onKidPress={handleKidPress}
        kidCard={kidCard}
      />

      {/* Lesson carousel – step 3 only */}
      {currentStep === 3 && (
        <LessonCarousel
          items={MOCK_GUARDIAN_LESSON_CAROUSEL_ITEMS}
          onViewDetailsPress={handleViewDetailsPress}
          onReschedulePress={handleReschedulePress}
          onLocationPress={handleLocationPress}
        />
      )}

      {/* Coming up this week – steps 2 & 3 */}
      {currentStep >= 2 && (
        <UpcomingWeekSection
          items={upcomingWeekItems}
          onBookClassPress={handleBookClassPress}
          onItemPress={handleUpcomingItemPress}
        />
      )}
    </Scroll>
  );
}

/** Guardian home tab with responsive dashboard panels. */
export function GuardianHomeScreen() {
  const navigation = useNavigation<GuardianHomeNavigation>();
  const hasUnreadNotification = useGuardianNotificationBadge();
  const handleNotificationPress = useCallback(() => {
    navigation.navigate('GuardianNotifications');
  }, [navigation]);

  const { isLoading } = useGuardianHomePlayers();
  const setGlobalLoading = useUiStore(s => s.setGlobalLoading);

  useEffect(() => {
    setGlobalLoading(isLoading);
    return () => setGlobalLoading(false);
  }, [isLoading, setGlobalLoading]);

  return (
    <Stack flex={1} backgroundColor={guardianColors['1']}>
      <UserProfileHeader
        hasUnreadNotification={hasUnreadNotification}
        onNotificationPress={handleNotificationPress}
      />
      {!isLoading && <GuardianHomeBody />}
    </Stack>
  );
}

const useStyles = createStyles(({ scale }) => ({
  tabletSecondScreen: {
    flex: 1,
    paddingTop: scale({ tablet: 0, phone: 16 }),
  },
  mobileScroll: {
    flex: 1,
  },
  tabletScroll: {
    flex: 1,
    minWidth: 0,
  },
  tabletScrollContent: {
    flexGrow: 1,
    width: '100%',
  },
  sectionTitleKids: {
    color: guardianColors['10'],
    paddingBottom: scale(8),
  },
}));
